import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// =================== Styles =====================
const validationSchema = yup.object().shape({
  name: yup.string().trim().required("Please Enter Permission Name."),
});

// Predefined list of menu permissions
const PREDEFINED_PERMISSIONS = [
  { value: "Dashboard", label: "Dashboard" },
  { value: "user Management", label: "User Management" },
  { value: "Roles & Permission", label: "Roles & Permission" },
  { value: "category & region", label: "Category & Region" },
  { value: "Course", label: "Course" },
  { value: "Cultural", label: "Cultural" },
  { value: "Terms & Privacy", label: "Terms & Privacy" },
];

const AddEditPermissionPage = () => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("");
  const [existingPermissions, setExistingPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (location.pathname.includes("/permissions/add")) setMode("add");
    else if (location.pathname.includes("/permissions/edit") && id) setMode("edit");
  }, [location.pathname, id]);

  // Fetch existing permissions to disable already added ones
  useEffect(() => {
    const fetchExistingPermissions = () => {
      setLoadingPermissions(true);
      axiosInstance
        .get(ApiEndPoints.PERMISSIONS.list)
        .then((response) => {
          const permissions = response.data?.data?.permission || [];
          setExistingPermissions(permissions);
        })
        .catch((error) => {
          toastError(error);
          setExistingPermissions([]);
        })
        .finally(() => {
          setLoadingPermissions(false);
        });
    };

    fetchExistingPermissions();
  }, []);

  useEffect(() => {
    const dataToEdit = location.state?.dataToEdit || {};

    if (mode === "edit" && dataToEdit?.name) {
      reset({
        name: dataToEdit?.name || "",
      });
    }
  }, [mode, location.state, reset]);

  const onSubmit = (data) => {
    const payload = {
      name: data.name,
    };

    setLoading(true);

    const apiInstance =
      mode === "edit"
        ? axiosInstance.patch(ApiEndPoints.PERMISSIONS.edit(id), payload)
        : axiosInstance.post(ApiEndPoints.PERMISSIONS.create, payload);

    apiInstance
      .then((response) => response.data)
      .then((response) => {
        toastSuccess(response.message);
        navigate("/permissions");
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Box
        component="form"
        id="permission-management-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 5, p: 5 }}
      >
        <Card>
          <CardContent>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton
                  sx={{
                    border: "2px solid",
                    borderColor: "primary.main",
                    p: 0.5,
                  }}
                  onClick={() => navigate(-1)}
                >
                  <ArrowBackIcon
                    sx={{ color: "primary.main" }}
                    fontSize="small"
                  />
                </IconButton>
                <Typography variant="h6">
                  {mode === "add" ? "Add New Permission" : "Edit Permission"}
                </Typography>
              </Box>
              <Divider />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth error={Boolean(errors.name)}>
                <FormLabel htmlFor="name" sx={{ color: "#000", mb: 1 }}>
                  Permission Name
                </FormLabel>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    // Get current permission being edited (if in edit mode)
                    const dataToEdit = location.state?.dataToEdit || {};
                    const currentPermissionName = mode === "edit" ? dataToEdit?.name : null;

                    // Get list of existing permission names (excluding current one being edited)
                    const existingPermissionNames = existingPermissions
                      .filter(perm => perm.name !== currentPermissionName)
                      .map(perm => perm.name);

                    return (
                      <Select
                        size="small"
                        id="name"
                        value={value || ""}
                        onChange={onChange}
                        displayEmpty
                        disabled={loadingPermissions}
                        input={
                          <OutlinedInput
                            sx={{
                              borderRadius: "8px",
                              backgroundColor: "rgba(0,0,0,0.04)",
                              minHeight: "40px",
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(0,0,0,0.06)",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                            }}
                          />
                        }
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: "8px",
                              mt: 1,
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select Permission Name</em>
                        </MenuItem>
                        {PREDEFINED_PERMISSIONS.map((permission) => {
                          const isDisabled = existingPermissionNames.includes(permission.value);
                          return (
                            <MenuItem
                              key={permission.value}
                              value={permission.value}
                              disabled={isDisabled}
                            >
                              {permission.label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    );
                  }}
                />
                {errors.name && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {errors.name.message}
                  </FormHelperText>
                )}
              </FormControl>

            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            form="permission-management-form"
            variant="contained"
            loading={loading}
          >
            {mode === "add" ? "Add" : "Save Changes"}
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};

export default AddEditPermissionPage;
